import React, { useRef, useState } from 'react';
import { ListItemText, MenuItem, Tooltip, Typography, Menu, Divider } from '@mui/material';
import { Checkbox } from '@mui/material';
import {
  addChartFilterGroupName,
  deleteChartFilterSelectedGroup,
  addChartFilterTabTileName,
  updateChartFilterSelectedGroups,
  updateChartFilterGroupsCollapsed,
} from '../../../redux/ChartFilterGroup/ChartFilterGroupStateActions';
import {
  addDashBoardFilterGroupTabTiles,
  setDashBoardFilterGroupsTabTiles,
  deleteDashBoardSelectedGroupAllTabTiles,
} from '../../../redux/DashBoardFilterGroup/DashBoardFilterGroupAction';
import {
  updateDashBoardGroups,
  deleteDashBoardSelectedGroup,
} from '../../../redux/DashBoardFilterGroup/DashBoardFilterGroupAction';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { useDispatch } from 'react-redux';
import DeleteFilterGroupDialog from './DeleteFilterGroupDialog';
import { callQueryAPI, SetModifyOptions } from '../../../redux/ChartPoperties/ChartControlsActions';
import { callQueryAPIDashboard } from '../../../redux/TabTile/TabTileActionsAndMultipleDispatches';

interface Props {
  propKey: string;
  fromDashboard: boolean;
  anchorEl: Element | null;
  setAnchorEl: React.Dispatch<React.SetStateAction<Element | null>>;
}

/**
 * rendered by ReportFilterHeaderWithGroupMenu.tsx
 * @returns a menu comprises available filter group names and a add button to add groups if screen is not dashboard screen.
 * Renders DeleteFilterGroupDialog.tsx component if openDialog state is true which becomes truthy when clicking delete icon.
 */
const ChartFilterGroupMenu = ({ propKey, fromDashboard, anchorEl, setAnchorEl }: Props) => {
  const [openDialog, setOpenDialog] = useState(false);

  const { chartProp, chartGroup, tileState, tabTileProps, tabState, dashBoardGroup } = useSelector(
    (state: RootState) => ({
      chartProp: state.chartProperties,
      chartGroup: state.chartFilterGroup,
      tileState: state.tileState,
      tabTileProps: state.tabTileProps,
      tabState: state.tabState,
      dashBoardGroup: state.dashBoardFilterGroup,
    }),
  );

  let selectedFilterGroups: any = fromDashboard
    ? dashBoardGroup.groups
    : chartGroup.tabTile[propKey] || [];

  const filterGroupToBeDeleted = useRef<{ id: string; name: string } | null>(null);
  let datasetGroupList: string[] = Object.keys(chartGroup.groups);
  const dispatch = useDispatch();

  let selectedDatasetID = !fromDashboard ? chartProp.properties[propKey].selectedDs.id : '';

  const getNewGroupName = (numOfGroups: number): string => {
    let isUnique = true;

    let newName = 'Filter Group ' + numOfGroups;

    Object.keys(chartGroup.groups).forEach((grp) => {
      if (chartGroup.groups[grp].name === newName) {
        isUnique = false;
        return;
      }
    });

    if (!isUnique) {
      return getNewGroupName(numOfGroups + 1);
    } else {
      return newName;
    }
  };

  const collapseOtherGroups = () => {
    if (selectedFilterGroups && selectedFilterGroups.length > 0) {
      selectedFilterGroups.forEach((grp: any) => {
        dispatch(updateChartFilterGroupsCollapsed(grp, true));
      });
    }
  };

  const handleCBChange = (event: any) => {
    if (chartGroup.groups[event.target.name].filters.length > 0) {
      dispatch(
        SetModifyOptions(
          propKey,
          'filter_group',
          `${event.target.name}_${chartGroup.groups[event.target.name].filters[0].uId}`,
        ),
      );
    } else {
      dispatch(SetModifyOptions(propKey, 'filter_group', null));
    }
    if (event.target.checked) {
      if (fromDashboard) {
        dispatch(addDashBoardFilterGroupTabTiles(event.target.name));
        dispatch(updateDashBoardGroups(event.target.name));

        let tabTilesList: any = [];
        let groupDataSetId = chartGroup.groups[event.target.name].dataSetId;

        [...tileState.tileList[tabTileProps.selectedTabId]].forEach((item: any) => {
          if (chartProp.properties[item].selectedDs.id === groupDataSetId) {
            tabTilesList.push(item);
          }
        });

        dispatch(setDashBoardFilterGroupsTabTiles(event.target.name, tabTilesList));
        if (chartGroup.groups[event.target.name].filters.length > 0) {
          tabState.tabs[tabTileProps.selectedTabId].tilesInDashboard.forEach(
            (tabTileId: string) => {
              dispatch(
                SetModifyOptions(
                  tabTileId,
                  'filter_group',
                  `${event.target.name}_${chartGroup.groups[event.target.name].filters[0].uId}`,
                ),
              );
              dispatch(callQueryAPI(tabTileId, true));
            },
          );
          dispatch(callQueryAPIDashboard(true));
        }
      } else {
        dispatch(updateChartFilterSelectedGroups(propKey, event.target.name));
        dispatch(callQueryAPI(propKey, true));
      }
    } else {
      if (fromDashboard) {
        dispatch(deleteDashBoardSelectedGroup(event.target.name));

        dispatch(deleteDashBoardSelectedGroupAllTabTiles(event.target.name));
        if (chartGroup.groups[event.target.name].filters.length > 0) {
          tabState.tabs[tabTileProps.selectedTabId].tilesInDashboard.forEach(
            (tabTileId: string) => {
              if (dashBoardGroup.filterGroupTabTiles[event.target.name].includes(tabTileId)) {
                dispatch(
                  SetModifyOptions(
                    tabTileId,
                    'filter_group',
                    `${event.target.name}_${chartGroup.groups[event.target.name].filters[0].uId}`,
                  ),
                );
                dispatch(callQueryAPI(tabTileId, true));
              }
            },
          );
          dispatch(callQueryAPIDashboard(true));
        }
      } else {
        dispatch(
          deleteChartFilterSelectedGroup(
            propKey,
            selectedFilterGroups.findIndex((name: string) => name === event.target.name),
          ),
        );
        dispatch(callQueryAPI(propKey, true));
      }
    }
  };

  return (
    <>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          setAnchorEl(null);
        }}
        slotProps={{
          paper: {
            style: {
              width: '28ch',
            },
          },
        }}
        sx={{}}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <>
          <MenuItem>
            <Typography
              variant="subtitle1"
              component="div"
              sx={{
                marginInline: '1rem',
                fontWeight: '700',
                color: 'text.secondary',
                marginBottom: '0.5rem',
              }}
            >
              Available Filter Groups
            </Typography>
          </MenuItem>
          {datasetGroupList?.length > 0 ? (
            datasetGroupList.map((filterGroupId: string, index: number) => {
              return (
                <MenuItem
                  key={index}
                  sx={{
                    height: '30px',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                  }}
                >
                  <label>
                    <Checkbox
                      size="small"
                      disabled={
                        fromDashboard
                          ? tabState.tabs[tabTileProps.selectedTabId].tilesInDashboard.length > 0
                            ? false
                            : true
                          : false
                      }
                      checked={selectedFilterGroups.includes(filterGroupId)}
                      name={filterGroupId}
                      onChange={(e) => {
                        handleCBChange(e);
                      }}
                      sx={{
                        '&.Mui-checked': {
                          color: '#2bb9bb',
                        },
                      }}
                    />
                  </label>
                  <ListItemText
                    primary={chartGroup.groups[filterGroupId].name}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: '60px',
                    }}
                  />
                  <Tooltip
                    title={datasetGroupList.length === 1 ? 'Disabled' : 'delete'}
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px',
                        },
                      },
                    }}
                  >
                    <IconButton
                      aria-label="delete"
                      sx={{
                        '&:hover .MuiSvgIcon-root': {
                          color: 'red',
                        },
                      }}
                      disabled={index === 0}
                      onClick={() => {
                        filterGroupToBeDeleted.current = {
                          id: filterGroupId,
                          name: chartGroup.groups[filterGroupId].name,
                        };
                        setOpenDialog(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </MenuItem>
              );
            })
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No Filter Group is available create a new one
            </Typography>
          )}
        </>
        {!fromDashboard ? (
          <>
            <Divider />
            <MenuItem
              sx={{
                backgroundColor: '#2bb9bb',
                '&:hover': {
                  backgroundColor: '#50d5d7',
                },
                marginInline: '1.8rem',
                paddingRight: '0',
                display: 'flex',
                justifyContent: 'center',
                gap: '0.5rem',
                borderRadius: '5px',
              }}
              onClick={(e) => {
                if (!(selectedFilterGroups && selectedFilterGroups.length > 0)) {
                  dispatch(addChartFilterTabTileName(selectedDatasetID, propKey));
                }
                let numOfGroups = 0;

                if (Object.keys(chartGroup.groups) && Object.keys(chartGroup.groups).length > 0) {
                  numOfGroups = Object.keys(chartGroup.groups).length;
                }

                let newGroupName = getNewGroupName(numOfGroups + 1);
                let groupId = selectedDatasetID + '_' + newGroupName + new Date().getMilliseconds();
                dispatch(addChartFilterGroupName(selectedDatasetID, groupId, newGroupName));
                collapseOtherGroups();
                dispatch(updateChartFilterSelectedGroups(propKey, groupId));
                dispatch(SetModifyOptions(propKey, 'filter_group', null));
                setAnchorEl(null);
              }}
            >
              <Typography variant="body2" sx={{ color: 'white' }}>
                Add New Group
              </Typography>
              <ListItemIcon>
                <AddIcon htmlColor="white" />
              </ListItemIcon>
            </MenuItem>
          </>
        ) : null}
      </Menu>
      <DeleteFilterGroupDialog
        filterGroupToBeDeleted={filterGroupToBeDeleted}
        datasetGroupList={datasetGroupList}
        setAnchorEl={setAnchorEl}
        openDialog={openDialog}
        setOpenDialog={setOpenDialog}
      />
    </>
  );
};

export default ChartFilterGroupMenu;
